import type {
  NormalizedTranscript,
  NormalizedTranscriptEntry,
  SessionDataAdapter,
} from "../sessionAdapter";
import { did, type DidExportedChat } from "./client";
import { readTranscript } from "./transcriptStore";
import { createLogger } from "@/lib/frontend-logger";

const logger = createLogger("did.sessionAdapter");

/**
 * D-ID session data adapter.
 *
 * Two ids are in play. The transcript buffer is keyed by the id the frame used
 * while the session ran (the stream session id, or a locally minted one), and
 * that is the id `useSessionTracking` passes here. D-ID's own records are keyed
 * by the *chat* id (`cht_...`), which the SDK reports through `onNewChat`. The
 * frame registers the pair below so this adapter can reach the vendor record.
 *
 * Meta comes from D-ID when the chat id is known (real duration, message count,
 * insights, recording status) and falls back to values derived from the local
 * buffer. The transcript is still captured live — that is the fastest and
 * always-available source — but when D-ID says the chat holds more messages
 * than we captured (a dropped tab, a missed event), the agent-wide chat-log
 * export is pulled as a backstop and the vendor's version wins.
 */

type SessionLink = { chatId: string; agentId?: string; startedAt: string };

const links = new Map<string, SessionLink>();

/** Called by the frame once D-ID reports the chat id for the running session. */
export function registerDidSession(input: {
  sessionId: string;
  chatId: string;
  agentId?: string;
}) {
  links.set(input.sessionId, {
    chatId: input.chatId,
    agentId: input.agentId,
    startedAt: new Date().toISOString(),
  });
}

export function forgetDidSession(sessionId: string) {
  links.delete(sessionId);
}

const EXPORT_POLL_ATTEMPTS = 10;
const EXPORT_POLL_INTERVAL_MS = 2000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function normalizeExportedChat(chat: DidExportedChat): NormalizedTranscriptEntry[] {
  const messages = Array.isArray(chat.messages) ? chat.messages : [];
  const times = messages.map((m) => Date.parse(String(m?.created_at ?? "")) || 0);
  const t0 = times.find((t) => t > 0) ?? 0;
  return messages
    .map((m, i) => {
      const text = String(m?.content ?? "").trim();
      if (!text) return null;
      const role = String(m?.role ?? "").toLowerCase() === "user" ? "user" : "avatar";
      const at = times[i] || t0;
      return {
        role,
        transcript: text,
        absolute_timestamp: at ? Math.round(at / 1000) : 0,
        relative_timestamp: at && t0 ? Math.round((at - t0) / 1000) : 0,
      } as NormalizedTranscriptEntry;
    })
    .filter((r): r is NormalizedTranscriptEntry => !!r);
}

/**
 * Pull the chat from D-ID's asynchronous, zipped chat-log export. Scoped to
 * the agent and the session's own time window so the archive stays small.
 * Returns `null` whenever the export is empty, still running, or does not
 * contain the chat — every one of those means "keep what we captured".
 */
async function fetchExportedChat(
  link: SessionLink,
): Promise<NormalizedTranscriptEntry[] | null> {
  if (!link.agentId) return null;
  const from = new Date(Date.parse(link.startedAt) - 5 * 60_000).toISOString();
  const created = await did.createChatExport({
    agent_id: link.agentId,
    from,
    to: new Date(Date.now() + 60_000).toISOString(),
  });
  if (!created.export_id) return null;
  for (let i = 0; i < EXPORT_POLL_ATTEMPTS; i++) {
    const res = await did.getChatExport(created.export_id);
    if (res.chats) {
      const match = res.chats.find((c) => c.chatId === link.chatId);
      if (!match) return null;
      const rows = normalizeExportedChat(match);
      return rows.length ? rows : null;
    }
    if (res.status === "error") return null;
    await sleep(EXPORT_POLL_INTERVAL_MS);
  }
  return null;
}

export const didSessionAdapter: SessionDataAdapter = {
  async fetchSessionMeta(sessionId) {
    const rows = readTranscript(sessionId);
    const last = rows[rows.length - 1];
    const local = {
      session_id: sessionId,
      duration: last ? last.relative_timestamp : 0,
      turns: rows.length,
      source: "local" as const,
    };
    const link = links.get(sessionId);
    if (!link) return local;
    try {
      const { session } = await did.getSession(link.chatId);
      if (!session) return { ...local, did_chat_id: link.chatId };
      return {
        ...local,
        source: "did",
        did_chat_id: link.chatId,
        did_agent_id: session.agent_id ?? link.agentId ?? null,
        duration:
          typeof session.duration_minutes === "number"
            ? Math.round(session.duration_minutes * 60)
            : local.duration,
        turns: session.message_count ?? local.turns,
        start_time: session.start_time ?? null,
        summary: session.summary ?? null,
        insights: session.insights ?? null,
        rating: session.rating ?? null,
        recording_status: session.recording_status ?? null,
      };
    } catch (e) {
      logger.warn("did session meta fetch failed", {
        session_id: sessionId,
        chat_id: link.chatId,
        error: (e as Error)?.message ?? String(e),
      });
      return { ...local, did_chat_id: link.chatId };
    }
  },

  async fetchTranscript(sessionId): Promise<NormalizedTranscript | null> {
    const rows = readTranscript(sessionId);
    const link = links.get(sessionId);
    if (!link) {
      return rows.length
        ? {
            session_active: false,
            transcript_data: rows,
            raw: { source: "did-local", turns: rows.length },
          }
        : null;
    }

    // Only reach for the export when the vendor knows about turns we don't.
    let vendorCount: number | null = null;
    try {
      const { session } = await did.getSession(link.chatId);
      vendorCount = typeof session?.message_count === "number" ? session.message_count : null;
    } catch {
      vendorCount = null;
    }

    if (vendorCount !== null && vendorCount > rows.length) {
      try {
        const exported = await fetchExportedChat(link);
        if (exported && exported.length >= rows.length) {
          logger.info("did transcript recovered from export", {
            session_id: sessionId,
            chat_id: link.chatId,
            local_turns: rows.length,
            vendor_turns: exported.length,
          });
          return {
            session_active: false,
            transcript_data: exported,
            raw: {
              source: "did-export",
              chat_id: link.chatId,
              turns: exported.length,
              local_turns: rows.length,
            },
          };
        }
      } catch (e) {
        logger.warn("did chat export backstop failed", {
          session_id: sessionId,
          chat_id: link.chatId,
          error: (e as Error)?.message ?? String(e),
        });
      }
    }

    if (!rows.length) return null;
    return {
      session_active: false,
      transcript_data: rows,
      raw: {
        source: "did-local",
        chat_id: link.chatId,
        turns: rows.length,
        vendor_turns: vendorCount,
      },
    };
  },
};
